import { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Header } from './Header';
import { Footer } from './Footer';
import { Aurora, CursorGlow, ScrollProgress } from './Atmosphere';

/**
 * Moves focus to the main region and resets scroll on navigation, so keyboard
 * and screen-reader users are not left at the bottom of the previous page.
 * In-page hash links are left alone.
 */
function useRouteChangeFocus(target: React.RefObject<HTMLElement | null>) {
  const { pathname, hash } = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (hash) return;

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    target.current?.focus({ preventScroll: true });
  }, [pathname, hash, target]);
}

export function Layout() {
  const mainRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  useRouteChangeFocus(mainRef);

  return (
    <div className="grain relative min-h-dvh">
      <Aurora />
      <CursorGlow />
      <ScrollProgress />

      <a
        href="#main"
        className="bg-brand sr-only rounded-full px-5 py-3 font-semibold text-white focus-visible:not-sr-only focus-visible:fixed focus-visible:top-4 focus-visible:left-4 focus-visible:z-[100]"
      >
        Skip to content
      </a>

      <Header />

      <div className="relative z-[2]">
        {/* The scene wrapper owns the perspective, so every section shares one
            vanishing point instead of each having its own. */}
        <main
          id="main"
          ref={mainRef}
          tabIndex={-1}
          className="animate-page-in scene mx-auto w-full max-w-[92rem] px-3 pt-4 outline-none sm:px-5 sm:pt-6"
          key={pathname}
        >
          <Outlet />
        </main>
        <Footer />
      </div>
    </div>
  );
}
